import React from 'react';

import {
  BufferConditionKeys,
  MeltingTempMethod,
  MeltingTempPreferences,
  NucleotideType,
} from 'biojs/BioConstants';
import MeltingTempModalDefaults from 'molbio/melting-temp/MeltingTempModalDefaults';
import MeltingTempAlgorithmDropdown from 'molbio/melting-temp/MeltingTempModalDropdown';
import Primer3MeltingTempParams from 'molbio/melting-temp/Primer3MeltingTempParams';
import SharedMeltingTempParams from 'molbio/melting-temp/SharedMeltingTempParams';

interface MeltingTempParamsProps {
  meltingTempOptions: MeltingTempPreferences;
  setMeltingTempOptions: (options: MeltingTempPreferences) => void;
  optionsHaveValidationError: {[key in BufferConditionKeys]: boolean};
  setOptionsHaveValidationError: (errors: {[key in BufferConditionKeys]: boolean}) => void;
  nucleotideType: NucleotideType;
}

/**
 * A wrapper around all primer melting temp parameter controls.
 *
 * @param meltingTempOptions The current values of melting temperature params.
 * @param setMeltingTempOptions A function to set the melting temperature params elsewhere in state.
 * @param optionsHaveValidationError Whether any of the melting temperature params have a validation error (i.e. are stringy)
 * @param setOptionsHaveValidationError A function to set the validation status in state.
 * @param nucleotideType Whether to render DNA or RNA primer params.
 * @constructor
 */
export default function MeltingTempParams({
  meltingTempOptions,
  setMeltingTempOptions,
  optionsHaveValidationError,
  setOptionsHaveValidationError,
  nucleotideType,
}: MeltingTempParamsProps): JSX.Element {
  return (
    <>
      <MeltingTempAlgorithmDropdown
        meltingTempOptions={meltingTempOptions}
        nucleotideType={nucleotideType}
        setMeltingTempOptions={setMeltingTempOptions}
      />
      <div>
        <SharedMeltingTempParams
          optionsHaveValidationError={optionsHaveValidationError}
          meltingTempOptions={meltingTempOptions}
          nucleotideType={nucleotideType}
          setMeltingTempOptions={setMeltingTempOptions}
          setOptionsHaveValidationError={setOptionsHaveValidationError}
        />
        {nucleotideType === NucleotideType.DNA && meltingTempOptions.method === MeltingTempMethod.primer3 && (
          <Primer3MeltingTempParams
            optionsHaveValidationError={optionsHaveValidationError}
            meltingTempOptions={meltingTempOptions}
            setMeltingTempOptions={setMeltingTempOptions}
            setOptionsHaveValidationError={setOptionsHaveValidationError}
          />
        )}
      </div>
      <MeltingTempModalDefaults
        meltingTempOptions={meltingTempOptions}
        nucleotideType={nucleotideType}
        setMeltingTempOptions={setMeltingTempOptions}
      />
    </>
  );
}
